import axios from 'axios'

// -------------------------------------------------------------------
// MAIN API – Backend API (cookies ON, tenant header controlled)
// -------------------------------------------------------------------
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true, // ✅ REQUIRED: always send cookies
})

// -------------------------------------------------------------------
// EXTERNAL API – Public APIs (no cookies, no tenant)
// -------------------------------------------------------------------
const externalApi = axios.create({
  withCredentials: false,
})

// -------------------------------------------------------------------
// Helper: determine if URL is external
// -------------------------------------------------------------------
const isExternal = (url) => {
  return url.startsWith('http://') || url.startsWith('https://')
}

// -------------------------------------------------------------------
// Cookie reader (CSRF support)
// -------------------------------------------------------------------
const getCookie = (name) => {
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }
  }
  return null
}

// -------------------------------------------------------------------
// Auth helpers (keep minimal; no cross-module state)
// -------------------------------------------------------------------
const isBadToken = (token) =>
  !token ||
  typeof token !== 'string' ||
  token === 'undefined' ||
  token === 'null' ||
  token.trim() === ''

const clearAuthStorage = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('activeTenantSubdomain')
  localStorage.removeItem('tenantSubdomain')
  delete api.defaults.headers.common.Authorization
}

const isPublicAuthEndpoint = (path) =>
  path === '/api/v1/auth/login' ||
  path === '/api/v1/auth/register' ||
  path === '/api/v1/auth/forgot-password' ||
  path === '/api/v1/auth/reset-password'

const isPublicSigningEndpoint = (path) =>
  path.startsWith('/api/v1/signing/public') ||
  path.startsWith('/api/v1/extrawork/signature') ||
  /\/api\/v1\/extrawork\/[^/]+\/(approve|reject|complete)$/.test(path)

// -------------------------------------------------------------------
// Helper: resolve tenant subdomain SAFELY
// -------------------------------------------------------------------
const resolveTenantSubdomain = () => {
  const isDev = import.meta.env.DEV

  // Default invalid tenants (prod-safe)
  const invalidTenants = new Set(['app', 'www', 'api'])

  // Dev-only additions (never bundled into prod)
  if (isDev) {
    invalidTenants.add('staging-app')
    invalidTenants.add('staging-api')
    invalidTenants.add('localhost')
  }

  // ❌ DO NOT derive tenant from hostname in prod
  if (isDev) {
    const hostname = window.location.hostname.toLowerCase()
    const sub = hostname.split('.')[0]
    if (!invalidTenants.has(sub)) {
      return sub
    }
  }

  // ✅ Explicit tenant selection ONLY
  return (
    localStorage.getItem('activeTenantSubdomain') || localStorage.getItem('tenantSubdomain') || null
  )
}

// -------------------------------------------------------------------
// REQUEST INTERCEPTOR – Cookies, Tenant, CSRF
// -------------------------------------------------------------------
api.interceptors.request.use((config) => {
  // Resolve the latest JWT token from localStorage and apply it
  const token = localStorage.getItem('token')
  // Only set Authorization when token is a non-empty, non-'undefined', non-'null' string
  if (token && token !== 'undefined' && token !== 'null') {
    config.headers['Authorization'] = `Bearer ${token}`
  } else {
    // ensure old header is not sent
    delete config.headers['Authorization']
  }

  // Determine the tenant subdomain using the helper. In dev this may derive from hostname;
  // in production it returns the explicit selection stored in localStorage.
  const tenantSubdomain = resolveTenantSubdomain()
  if (tenantSubdomain) {
    config.headers['X-Tenant-Subdomain'] = tenantSubdomain
  } else {
    // If no tenant subdomain (very unlikely), ensure the header is removed
    delete config.headers['X-Tenant-Subdomain']
  }

  // CSRF token logic remains unchanged...
  const csrfToken = getCookie('XSRF-TOKEN')
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase())) {
    config.headers['X-XSRF-TOKEN'] = csrfToken
  }

  return config
})

// -------------------------------------------------------------------
// RESPONSE INTERCEPTOR – SAFE 401 HANDLING
// -------------------------------------------------------------------

// Prevent redirect storms (many concurrent 401s) without permanently disabling future redirects
let _isUnauthorizedRedirecting = false

// Normalize axios config.url (relative/absolute) into a safe pathname for allowlist checks
const getRequestPathname = (config) => {
  const rawUrl = config?.url || ''
  try {
    const base = config?.baseURL || api?.defaults?.baseURL || window.location.origin
    const u = new URL(rawUrl, base)
    return u.pathname
  } catch {
    return rawUrl.split('?')[0]
  }
}

// Only redirect if the failing request was an authenticated request
const wasAuthenticatedRequest = (config) => {
  const headers = config?.headers || {}
  const auth = headers.Authorization || headers.authorization || null
  if (typeof auth === 'string' && auth.trim() !== '') return true

  // Fallback: token exists in storage
  const token = localStorage.getItem('token')
  return !isBadToken(token)
}

const redirectToLogin = (nextPath) => {
  const target = nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : '/login'
  window.location.assign(target)
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const config = error?.config
    const path = getRequestPathname(config)

    /**
     * 🚨 Force logout on authenticated 401s only.
     * Avoid public/auth endpoints and public signing flows.
     */
    const shouldRedirect =
      status === 401 &&
      !_isUnauthorizedRedirecting &&
      wasAuthenticatedRequest(config) &&
      !isPublicAuthEndpoint(path) &&
      !isPublicSigningEndpoint(path) &&
      window.location.pathname !== '/login'

    if (shouldRedirect) {
      _isUnauthorizedRedirecting = true

      // Safety valve: if the app uses SPA routing (no full reload), don't permanently lock redirects
      setTimeout(() => {
        _isUnauthorizedRedirecting = false
      }, 15000)

      clearAuthStorage()

      const currentPath = window.location.pathname + window.location.search

      // Preserve any existing SPA listener behavior...
      try {
        const event = new CustomEvent('unauthorized-redirect', {
          detail: { nextPath: currentPath },
        })
        window.dispatchEvent(event)
      } catch (_) {
        // no-op
      }

      // ...but also guarantee navigation even if no listener exists.
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          redirectToLogin(currentPath)
        }
      }, 0)
    }

    return Promise.reject(error)
  },
)

// -------------------------------------------------------------------
// MAIN EXPORT FUNCTIONS – SAME API (NO BREAKING CHANGES)
// -------------------------------------------------------------------
export const GetData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.get(endPoint, options)
}

export const PostData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.post(endPoint, options)
}

export const PutData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.put(endPoint, options)
}

export const PatchData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.patch(endPoint, options)
}

export const DeleteData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.delete(endPoint, options)
}

// -------------------------------------------------------------------
// DOWNLOAD DATA (unchanged behavior)
// -------------------------------------------------------------------
export const DownloadData = async (
  endPoint,
  filename = 'download',
  fallbackMimeType = 'application/octet-stream',
) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.get(endPoint, { responseType: 'blob' })

    const mimeType = response.headers['content-type'] || fallbackMimeType
    const extension = getFileExtensionFromMimeType(mimeType)

    const blob = new Blob([response.data], { type: mimeType })
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith(`.${extension}`) ? filename : `${filename}.${extension}`
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
    alert('Failed to download file.')
  }
}

// -------------------------------------------------------------------
// MIME → extension map (unchanged)
// -------------------------------------------------------------------
const getFileExtensionFromMimeType = (mimeType) => {
  const map = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'text/csv': 'csv',
    'application/zip': 'zip',
    'application/json': 'json',
  }

  return map[mimeType] || 'bin'
}

export default api
